import type { CSSProperties, ReactNode } from 'react';

/**
 * Shared building blocks for the Today desktop v2 screen (design/screens/
 * today-desktop-v2.md), extracted so every card file reuses the exact same
 * card chrome / label / status-pill visual language rather than a parallel
 * copy. No domain-aware logic lives here — plain presentation only.
 */

/** `elevation.1` card shell (SS1: `surface.raised`, `radius.xl`,
 *  `border.subtle`, `space.5` padding) — every card on this screen. */
export function TodayCard({
  label,
  children,
  style,
}: {
  readonly label: string;
  readonly children: ReactNode;
  readonly style?: CSSProperties;
}) {
  return (
    <section
      aria-label={label}
      style={{
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-xl)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        ...style,
      }}
    >
      {children}
    </section>
  );
}

/** Card title (`type.heading`, `text.primary`) — plain noun-phrase card titles. */
export function CardTitle({ children }: { readonly children: ReactNode }) {
  return (
    <h2 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
      {children}
    </h2>
  );
}

/** `type.label` uppercase-tracked `text.muted` micro-header — reused across every card zone. */
export function MicroLabel({ children }: { readonly children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 'var(--type-label-size)',
        lineHeight: 'var(--type-label-line)',
        letterSpacing: 'var(--type-label-tracking)',
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
        fontWeight: 600,
      }}
    >
      {children}
    </div>
  );
}

/** Quiet centered empty-state caption, reused by every card's documented empty state. */
export function EmptyCaption({ children }: { readonly children: ReactNode }) {
  return (
    <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
      {children}
    </p>
  );
}

/**
 * Status-dot pill (design/screens/today-desktop-v2.md SS5) — the exact
 * `AutosaveStatusPill` draft-dot construction, extracted into its own shared
 * primitive (spec's explicitly-allowed implementation choice) rather than
 * duplicated. The ONLY consumer this phase is the Next-up card's DRAFT badge,
 * but this is generic over dot color / label so it never grows a second,
 * near-identical pill component if a future screen needs the same shape.
 */
export function StatusDotPill({ label, dotColor }: { readonly label: string; readonly dotColor: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        fontSize: 'var(--type-caption-size)',
        fontWeight: 600,
        color: 'var(--text-secondary)',
        background: 'var(--surface-hover)',
        borderRadius: 'var(--radius-pill)',
        padding: '2px 10px',
      }}
    >
      <span aria-hidden="true" style={{ width: '6px', height: '6px', borderRadius: '50%', background: dotColor, flex: 'none' }} />
      {label}
    </span>
  );
}

/** Single-line text input matching `inputStyle`'s chrome, sized for inline capture rows. */
export const todayInputStyle: CSSProperties = {
  width: '100%',
  minHeight: 'var(--touch-target-min)',
  padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-subtle)',
  background: 'var(--surface-sunken)',
  color: 'var(--text-primary)',
  fontSize: 'var(--type-body-size)',
  fontFamily: 'var(--font-family)',
};

/** Objective-accent chip (colored dot + name) — reused by Priority rows, Next-up rows.
 *  Renders as a plain clickable button when `onSelect` is passed (navigates to
 *  the linked objective), or inert chrome-only when omitted. */
export function ObjectiveAccentChip({
  title,
  tokenPrefix,
  onSelect,
}: {
  readonly title: string;
  readonly tokenPrefix: string | null;
  readonly onSelect?: () => void;
}) {
  const dot = (
    <span
      aria-hidden="true"
      style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: tokenPrefix ? `var(${tokenPrefix}-vivid)` : 'var(--text-muted)',
        flex: 'none',
      }}
    />
  );

  const chipStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    fontSize: 'var(--type-caption-size)',
    color: 'var(--text-secondary)',
    background: 'var(--surface-hover)',
    borderRadius: 'var(--radius-pill)',
    padding: '2px 10px',
    maxWidth: '100%',
  };

  if (onSelect) {
    return (
      <button type="button" onClick={onSelect} aria-label={`Open ${title}`} style={chipStyle}>
        {dot}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
      </button>
    );
  }

  return (
    <span style={chipStyle}>
      {dot}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
    </span>
  );
}
